import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, Check, DollarSign, Users } from "lucide-react";

const DISC_LABELS = {
  jiu_jitsu_bresilien: "JJB", jujutsu_japonais: "Can Ryu",
  muay_thai_kickboxing: "Kick/Muay", power_kicker: "Powerkick", autre: "Autre",
};

export default function MembresFacturation() {
  const [members, setMembers] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [sel, setSel] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [linkId, setLinkId] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);

  const sendAllCardLinks = async () => {
    const withoutCard = members.filter((m) => !m.has_card && m.email).length;
    if (!withoutCard) { toast.info("Tous les membres avec courriel ont déjà une carte."); return; }
    if (!window.confirm(`Envoyer le lien d'enregistrement de carte à ${withoutCard} membre(s) sans carte ?`)) return;
    setBulkSending(true);
    try {
      const { data } = await api.post("/member-payments/send-card-links-bulk");
      if (data.quota_reached) toast.warning(`${data.sent} envoyés — quota Resend atteint. Reste ${data.remaining} à envoyer demain.`);
      else toast.success(`${data.sent} lien(s) carte envoyé(s) ✓${data.failed ? ` · ${data.failed} échec(s)` : ""}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi groupé"); }
    finally { setBulkSending(false); }
  };

  const sendCardLink = async (m) => {
    if (!m.email) { toast.error("Ce membre n'a pas de courriel"); return; }
    setLinkId(m.id);
    try {
      await api.post(`/member-payments/members/${encodeURIComponent(m.email)}/send-card-link`, { member_name: m.full_name });
      toast.success(`Lien d'enregistrement de carte envoyé à ${m.email} ✓`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi du lien"); }
    finally { setLinkId(null); }
  };

  const load = async () => {
    const [m, t] = await Promise.all([
      api.get("/members/billing/members"),
      api.get("/dojo-tariffs?active_only=true"),
    ]);
    setMembers(m.data.members || []);
    setTariffs(t.data || []);
  };
  useEffect(() => { load().catch(() => toast.error("Erreur de chargement")); }, []);

  const assign = async (memberId) => {
    const tariffId = sel[memberId];
    if (!tariffId) { toast.error("Choisis un forfait"); return; }
    setSavingId(memberId);
    try {
      const { data } = await api.post("/members/billing/assign", { member_id: memberId, tariff_id: tariffId });
      toast.success(`Forfait assigné · ${data.tarif_mensuel} $ / ${DISC_LABELS[data.discipline] || data.discipline} ✓`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'assignation"); }
    finally { setSavingId(null); }
  };

  const stats = useMemo(() => {
    const withTarif = members.filter((m) => (m.tarif_mensuel || 0) > 0).length;
    const withCard = members.filter((m) => m.has_card).length;
    const total = members.reduce((s, m) => s + (m.tarif_mensuel || 0), 0);
    return { withTarif, withCard, mrr: total };
  }, [members]);

  return (
    <div className="p-6 text-zinc-100 max-w-6xl mx-auto" data-testid="membres-facturation">
      <h1 className="text-3xl font-bold flex items-center gap-2"><CreditCard className="text-emerald-400" /> Facturation membres — forfaits</h1>
      <p className="text-zinc-400 text-sm mt-1">Relie chaque membre à un forfait de ta grille (fixe le tarif mensuel + la discipline). Ensuite, le membre enregistre sa carte via le portail et tu prélèves depuis « Paiements Membres ».</p>

      <div className="flex flex-wrap gap-4 mt-4">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3" data-testid="stat-members"><div className="text-2xl font-bold text-emerald-400">{members.length}</div><div className="text-xs text-zinc-400"><Users size={12} className="inline mr-1" />membres actifs</div></div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3" data-testid="stat-tarif"><div className="text-2xl font-bold text-emerald-300">{stats.withTarif}</div><div className="text-xs text-zinc-400">avec forfait/tarif</div></div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3" data-testid="stat-cards"><div className="text-2xl font-bold text-sky-400">{stats.withCard}</div><div className="text-xs text-zinc-400">carte enregistrée</div></div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3" data-testid="stat-mrr"><div className="text-2xl font-bold text-amber-400">{stats.mrr.toFixed(0)} $</div><div className="text-xs text-zinc-400"><DollarSign size={12} className="inline" />potentiel mensuel</div></div>
      </div>

      <div className="flex flex-wrap gap-4 mt-4 items-center">
        <button onClick={sendAllCardLinks} disabled={bulkSending} data-testid="send-all-card-links-btn"
          className="flex items-center gap-2 bg-sky-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-sky-500 disabled:opacity-50 text-sm">
          <CreditCard size={16} /> {bulkSending ? "Envoi en cours…" : "Envoyer les liens carte à tous (sans carte)"}</button>
        <span className="text-xs text-amber-400">⚠️ Quota Resend limité (~10/jour) — les envois au-delà se feront le lendemain.</span>
      </div>

      <div className="mt-5 rounded-xl border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm" data-testid="members-billing-table">
          <thead className="bg-zinc-900 text-zinc-400">
            <tr>
              <th className="text-left px-3 py-2">Membre</th>
              <th className="text-left px-3 py-2">Indice ZenPlanner</th>
              <th className="text-left px-3 py-2">Tarif actuel</th>
              <th className="text-left px-3 py-2">Carte</th>
              <th className="text-left px-3 py-2">Assigner un forfait</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-t border-zinc-800 align-top" data-testid={`member-row-${m.id}`}>
                <td className="px-3 py-2"><div className="font-medium">{m.full_name}</div><div className="text-xs text-zinc-500">{m.email || "—"}</div></td>
                <td className="px-3 py-2 text-xs text-zinc-400 max-w-[200px]">{m.zp_membership_label || "—"}</td>
                <td className="px-3 py-2">{(m.tarif_mensuel || 0) > 0 ? <span className="text-emerald-300 font-semibold">{m.tarif_mensuel} $</span> : <span className="text-zinc-500">—</span>}<div className="text-xs text-zinc-500">{DISC_LABELS[m.discipline] || m.discipline}</div></td>
                <td className="px-3 py-2">{m.has_card ? <Check size={16} className="text-emerald-400" /> : (
                  <button onClick={() => sendCardLink(m)} disabled={linkId === m.id || !m.email} data-testid={`send-card-link-${m.id}`}
                    className="text-xs bg-sky-700/40 border border-sky-600 text-sky-200 px-2 py-1 rounded hover:bg-sky-700/70 disabled:opacity-40 whitespace-nowrap">
                    {linkId === m.id ? "…" : "Envoyer lien carte"}</button>
                )}</td>
                <td className="px-3 py-2">
                  <select value={sel[m.id] || ""} onChange={(e) => setSel((s) => ({ ...s, [m.id]: e.target.value }))} data-testid={`tariff-select-${m.id}`}
                    className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 max-w-[280px]">
                    <option value="">— choisir un forfait —</option>
                    {tariffs.map((t) => <option key={t.id} value={t.id}>{t.label} · {t.price} $</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => assign(m.id)} disabled={savingId === m.id || !sel[m.id]} data-testid={`assign-btn-${m.id}`}
                    className="bg-emerald-600 text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-emerald-500 disabled:opacity-40 text-xs whitespace-nowrap">
                    {savingId === m.id ? "…" : "Assigner"}</button>
                </td>
              </tr>
            ))}
            {members.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-zinc-500">Aucun membre actif importé.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
